// Organization settings shell — header + OrgSubnav pill row.
// Each tab sets title/subtitle via useOrgPageMeta() and renders its body through <Outlet>.
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import OrgSubnav from '@/components/layout/OrgSubnav';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { OrgMetaContext, useOrgSlug, type OrgPageMeta } from './orgSettingsMeta';
import '@/styles/pages/org-settings.css';

export default function OrgSettingsLayout() {
  const slug = useOrgSlug();
  // Drive the tab row from the permission map, not `useAuth().isOrgAdmin` (which is
  // `orgRole` OR the platform-staff flag, read straight off the store). Using can() means the
  // row reflects the server's own answer and honours the View-as preview — with the raw flag a
  // platform admin previewing as a member still saw every admin tab.
  const { can } = usePermissions();
  const isOrgAdmin = can('org:manage_members');
  const [meta, setMeta] = useState<OrgPageMeta>({ title: '' });

  return (
    <OrgMetaContext.Provider value={{ setMeta }}>
      <div className="org-settings-page">
        <header className="org-header">
          <div className="org-header-row">
            <div className="org-header-copy">
              <h1 className="org-title">{meta.title || 'Organization'}</h1>
              {meta.subtitle ? <div className="org-subtitle">{meta.subtitle}</div> : null}
            </div>
            {isOrgAdmin ? (
              <NavLink
                to={paths.org.edit(slug)}
                end
                className={({ isActive }) => (isActive ? 'org-icon-btn active' : 'org-icon-btn')}
                aria-label="Organization settings"
              >
                <i className="bi bi-gear" aria-hidden="true" />
              </NavLink>
            ) : null}
          </div>
        </header>

        <OrgSubnav orgSlug={slug} isOrgAdmin={isOrgAdmin} />

        <Outlet />
      </div>
    </OrgMetaContext.Provider>
  );
}
